import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('pulse_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('pulse_token');
      localStorage.removeItem('pulse_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  verifyRegistration: (data) => api.post('/auth/verify-registration', data),
  resendVerification: (data) => api.post('/auth/resend-verification', data),
};

// Organization
export const organizationAPI = {
  get: () => api.get('/organization'),
};

// Projects
export const projectsAPI = {
  list: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  toggleActive: (id) => api.post(`/projects/${id}/toggle-active`),
  dashboard: (id) => api.get(`/projects/${id}/dashboard`),
  network: (id) => api.get(`/projects/${id}/network`),
  peopleGalaxy: (id) => api.get(`/projects/${id}/people-galaxy`),
  responses: (id, params) => api.get(`/projects/${id}/responses`, { params }),
  aiSummary: (id, driverId) => api.get(`/projects/${id}/ai/summary`, { params: { driver_id: driverId } }),
  listWithRoles: () => api.get('/projects-with-roles'),
};

// Drivers
export const driversAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/drivers`),
  create: (projectId, data) => api.post(`/projects/${projectId}/drivers`, data),
  update: (projectId, id, data) => api.put(`/projects/${projectId}/drivers/${id}`, data),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/drivers/${id}`),
  reorder: (projectId, driverIds) => api.post(`/projects/${projectId}/drivers/reorder`, { driver_ids: driverIds }),
  importQuickStart: (projectId) => api.post(`/projects/${projectId}/drivers/import-quick-start`),
  importFullQuickStart: (projectId) => api.post(`/projects/${projectId}/import-full-quick-start`),
  getQuickStartStatus: (projectId) => api.get(`/projects/${projectId}/quick-start-status`),
};

// Segment Types
export const segmentTypesAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/segment-types`),
  create: (projectId, data) => api.post(`/projects/${projectId}/segment-types`, data),
  update: (projectId, id, data) => api.put(`/projects/${projectId}/segment-types/${id}`, data),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/segment-types/${id}`),
};

// Segments
export const segmentsAPI = {
  list: (projectId, segmentTypeId) => api.get(`/projects/${projectId}/segments`, { params: { segment_type_id: segmentTypeId } }),
  listWithTypes: (projectId) => api.get(`/projects/${projectId}/segments-with-types`),
  create: (projectId, data) => api.post(`/projects/${projectId}/segments`, data),
  update: (projectId, id, data) => api.put(`/projects/${projectId}/segments/${id}`, data),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/segments/${id}`),
};

// Questions
export const questionsAPI = {
  list: (projectId, driverId, questionContext) => {
    const params = {};
    if (driverId) params.driver_id = driverId;
    if (questionContext) params.question_context = questionContext;
    return api.get(`/projects/${projectId}/questions`, { params });
  },
  create: (projectId, data) => api.post(`/projects/${projectId}/questions`, data),
  update: (projectId, id, data) => api.put(`/projects/${projectId}/questions/${id}`, data),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/questions/${id}`),
  reorder: (projectId, questionIds) => api.post(`/projects/${projectId}/questions/reorder`, { question_ids: questionIds }),
  importQuickStart: (projectId, context) => api.post(`/projects/${projectId}/questions/import-quick-start`, { context }),
  getResponseStats: (projectId) => api.get(`/projects/${projectId}/questions/response-stats`),
};

// My Responses (About Me)
export const myResponsesAPI = {
  get: (projectId) => api.get(`/projects/${projectId}/my-responses`),
  save: (projectId, responses) => api.post(`/projects/${projectId}/my-responses`, { responses }),
};

// My Questions (filtered by user's segments)
export const myQuestionsAPI = {
  get: (projectId, questionContext = 'about_me') => api.get(`/projects/${projectId}/my-questions`, { params: { question_context: questionContext } }),
};

// Stakeholders
export const stakeholdersAPI = {
  list: (projectId, params = {}) => api.get(`/projects/${projectId}/stakeholders`, { params }),
  listAll: (projectId) => api.get(`/projects/${projectId}/stakeholders`, { params: { page_size: 10000 } }),
  create: (projectId, data) => api.post(`/projects/${projectId}/stakeholders`, data),
  update: (projectId, id, data) => api.put(`/projects/${projectId}/stakeholders/${id}`, data),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/stakeholders/${id}`),
  approve: (projectId, id) => api.post(`/projects/${projectId}/stakeholders/${id}/approve`),
  reject: (projectId, id) => api.post(`/projects/${projectId}/stakeholders/${id}/reject`),
  promoteAdmin: (projectId, id) => api.post(`/projects/${projectId}/stakeholders/${id}/promote-admin`),
  demoteAdmin: (projectId, id) => api.post(`/projects/${projectId}/stakeholders/${id}/demote-admin`),
  export: (projectId) => api.get(`/projects/${projectId}/stakeholders/export`, { responseType: 'blob' }),
  import: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/stakeholders/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Public stakeholder stats
  getPublicStats: (projectId, period = 'daily') => 
    api.get(`/projects/${projectId}/stakeholders/public-stats`, { params: { period } }),
  // Invited user stats
  getInvitedStats: (projectId) => 
    api.get(`/projects/${projectId}/stakeholders/invited-stats`),
  // Duplicate detection & merge
  checkDuplicates: (projectId, stakeholderId) => 
    api.get(`/projects/${projectId}/stakeholders/${stakeholderId}/check-duplicates`),
  merge: (projectId, data) => 
    api.post(`/projects/${projectId}/stakeholders/merge`, data),
  // Get stakeholders pending review with duplicate info
  getPendingReview: (projectId) => 
    api.get(`/projects/${projectId}/stakeholders/pending-review`),
};

// Invitations
export const invitationsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/invitations`),
  create: (projectId, stakeholderId) => api.post(`/projects/${projectId}/invitations`, { stakeholder_id: stakeholderId }),
  bulk: (projectId, stakeholderIds) => api.post(`/projects/${projectId}/invitations/bulk`, stakeholderIds),
  resend: (projectId, id) => api.post(`/projects/${projectId}/invitations/${id}/resend`),
};

// Dashboard
export const dashboardAPI = {
  org: () => api.get('/dashboard'),
};

// Public Survey APIs (no auth)
const publicApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

export const surveyAPI = {
  get: (token) => publicApi.get(`/survey/${token}`),
  submit: (token, responses) => publicApi.post(`/survey/${token}/submit`, { responses }),
  createRelationship: (token, data) => publicApi.post(`/survey/${token}/relationships`, data),
};

export const publicAPI = {
  getSegments: (projectId) => publicApi.get(`/public/project/${projectId}/segments`),
  selfIdentify: (projectId, data) => publicApi.post(`/public/project/${projectId}/self-identify`, data),
  verifyEmail: (token) => publicApi.post('/public/verify-email', { token }),
};

// Public Landing Config
export const publicLandingAPI = {
  getConfig: (projectId) => api.get(`/projects/${projectId}/public-landing-config`),
  updateConfig: (projectId, data) => api.put(`/projects/${projectId}/public-landing-config`, data),
};

// File Upload
export const uploadAPI = {
  upload: (projectId, file, fileType) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/upload?file_type=${fileType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Pending Identifications
export const pendingIdentificationsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/pending-identifications`),
  approve: (projectId, pendingId) => api.post(`/projects/${projectId}/pending-identifications/${pendingId}/approve`),
  reject: (projectId, pendingId) => api.post(`/projects/${projectId}/pending-identifications/${pendingId}/reject`),
};

// Question Library (for importing into projects)
export const questionLibraryAPI = {
  getAboutMe: () => api.get('/question-library/about_me'),
  getAboutOthers: () => api.get('/question-library/about_others'),
  getQuickStart: (context) => api.get(`/question-library/${context}/quick-start`),
};

// Mapping Categories Library (Global Admin)
export const mappingCategoriesLibraryAPI = {
  list: () => api.get('/admin/mapping-categories'),
  create: (data) => api.post('/admin/mapping-categories', data),
  update: (id, data) => api.put(`/admin/mapping-categories/${id}`, data),
  delete: (id) => api.delete(`/admin/mapping-categories/${id}`),
  reorder: (categoryIds) => api.post('/admin/mapping-categories/reorder', { category_ids: categoryIds }),
  // Public endpoint for users
  getForUsers: () => api.get('/mapping-categories-library'),
};

// Project Mapping Categories
export const projectMappingCategoriesAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/mapping-categories`),
  create: (projectId, data) => api.post(`/projects/${projectId}/mapping-categories`, data),
  update: (projectId, id, data) => api.put(`/projects/${projectId}/mapping-categories/${id}`, data),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/mapping-categories/${id}`),
  importFromLibrary: (projectId, categoryIds) => api.post(`/projects/${projectId}/mapping-categories/import`, categoryIds),
};

// About Others
export const aboutOthersAPI = {
  // Selections (stakeholders/segments the user has chosen)
  getSelections: (projectId) => api.get(`/projects/${projectId}/about-others/selections`),
  createSelection: (projectId, data) => api.post(`/projects/${projectId}/about-others/selections`, data),
  updateSelection: (projectId, selectionId, data) => api.put(`/projects/${projectId}/about-others/selections/${selectionId}`, data),
  deleteSelection: (projectId, selectionId) => api.delete(`/projects/${projectId}/about-others/selections/${selectionId}`),
  // Add new stakeholder (user-identified)
  addNewStakeholder: (projectId, data) => api.post(`/projects/${projectId}/about-others/add-new-stakeholder`, data),
  // Responses
  getResponses: (projectId, selectionId) => api.get(`/projects/${projectId}/about-others/responses`, { params: { selection_id: selectionId } }),
  saveResponses: (projectId, responses) => api.post(`/projects/${projectId}/about-others/responses`, { responses }),
  // Galaxy visualization data
  getGalaxyData: (projectId) => api.get(`/projects/${projectId}/about-others/galaxy-data`),
  // Recommended stakeholders
  getRecommendations: (projectId) => api.get(`/projects/${projectId}/recommended-stakeholders`),
};

// Public Landing Page (no auth)
export const publicLandingAPI2 = {
  getLanding: (projectId) => publicApi.get(`/public/project/${projectId}/landing`),
};

export default api;
